import { useState } from "react";

import { type Compare } from "@features/sales-v3/hooks/use-chart-state";
import { type GetCompareQueryMetadata } from "@features/sales-v3/utils/compare-query";
import {
  safePolygon,
  useDelayGroup,
  useFloating,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { MCAttributionWindowDays } from "@north-beam/nb-common";
import { useAttributionWindowOptions } from "@pages/sales/report-body-control/attribution-window-select";

import { MenuItem } from "./menu-item";
import { MenuSubmenuItem } from "./menu-submenu-item";

interface AttributionWindowSelectorProps {
  compare: Compare;
  updateCompare: (compare: Compare) => void;
  getCompareQueryMetadata: GetCompareQueryMetadata;
  isDisabled?: boolean;
}

export const AttributionWindowSelector = ({
  compare,
  updateCompare,
  getCompareQueryMetadata,
  isDisabled = false,
}: AttributionWindowSelectorProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const compareQueryMetadata = getCompareQueryMetadata({
    queryKey: "attributionWindow",
  });

  const { refs, floatingStyles, context } = useFloating({
    open: isHovering,
    onOpenChange: setIsHovering,
    transform: false,
    placement: "right-start",
  });

  const { delay } = useDelayGroup(context);

  const hover = useHover(context, {
    delay,
    handleClose: safePolygon({
      requireIntent: false,
      blockPointerEvents: true,
      buffer: 1,
    }),
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);
  const { options: initialOptions } = useAttributionWindowOptions({
    timeGranularity: "daily",
    includeInfinity: true,
  });
  return (
    <div>
      <MenuItem
        isDisabled={isDisabled}
        isHovering={isHovering}
        icon={<span className="mr-2">{compareQueryMetadata.icon}</span>}
        text={compareQueryMetadata.label}
        setReference={refs.setReference}
        {...getReferenceProps()}
      />
      {isHovering && (
        <div
          className="absolute rounded-md bg-white elevation-1 py-1 z-10 text-sm w-56"
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {initialOptions.map((option) => (
            <MenuSubmenuItem
              key={option.value}
              label={option.label}
              isSelected={
                compare.queryKey === "attributionWindow" &&
                compare.queryValue === option.value
              }
              onClick={() =>
                updateCompare({
                  ...compare,
                  queryKey: "attributionWindow",
                  queryValue: option.value,
                })
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};
